import React, {useCallback} from 'react'
import {useDispatch} from 'react-redux'

import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'

export const MyListLinkComponent = {
  DesktopHeader: 'DesktopHeader',
  HomeDesktopHeader: 'HomeDesktopHeader',
  MobileHeader: 'MobileHeader',
  HomeMobileHeader: 'HomeMobileHeader',
  UserMenu: 'UserMenu',
  MobileMenu: 'MobileMenu'
} as const

export type MyListLinkComponent =
  (typeof MyListLinkComponent)[keyof typeof MyListLinkComponent]

interface Params {
  component: MyListLinkComponent
  isAuthenticated: boolean
  href?: string
}

const opensInThisTab = ({nativeEvent}: React.SyntheticEvent) =>
  nativeEvent instanceof MouseEvent &&
  nativeEvent.button === 0 &&
  !nativeEvent.metaKey &&
  !nativeEvent.ctrlKey &&
  !nativeEvent.shiftKey &&
  !nativeEvent.altKey

/**
 * Tracks a click on any My List entry point. Analytics sends without a beacon,
 * so a plain click waits briefly before navigating. A click that opens a new
 * tab keeps this page alive and goes through untouched.
 */
export const useTrackMyListLinkClick = ({
  component,
  isAuthenticated,
  href
}: Params) => {
  const dispatch = useDispatch()

  return useCallback(
    (event?: React.SyntheticEvent) => {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.MyListLink,
          action: Action.Clicked,
          team: Team.Retention,
          payload: {isUserSignedIn: isAuthenticated},
          component
        })
      )

      if (!href || !event || !opensInThisTab(event)) return

      event.preventDefault()
      setTimeout(
        () => navigateTo(href),
        TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
      )
    },
    [dispatch, component, isAuthenticated, href]
  )
}
